import { ROLES, usuarioCrearSchema, type UsuarioCrearInput } from '@app/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Copy } from 'lucide-react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { Alert } from '../../../components/ui/alert';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { FormField } from '../../../components/ui/form-field';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { useI18n } from '../../../i18n/I18nProvider';
import { getErrorCode, getErrorMessage } from '../../../lib/api';
import { qk } from '../../../lib/queryKeys';
import { usuariosApi } from '../api/usuarios.api';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UsuarioCrearDialog({ open, onOpenChange }: Props): JSX.Element {
  const queryClient = useQueryClient();
  const [passwordTemporal, setPasswordTemporal] = useState<string | null>(null);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const es = useI18n();

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<UsuarioCrearInput>({
    resolver: zodResolver(usuarioCrearSchema),
    defaultValues: { rol: 'COLABORADOR' },
  });

  const mutation = useMutation({
    mutationFn: usuariosApi.create,
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: qk.usuarios.all });
      if (result.passwordTemporal) {
        setPasswordTemporal(result.passwordTemporal);
        toast.success(es.admin.usuarios.crear_dialog.exito);
      } else {
        toast.success(es.admin.usuarios.crear_dialog.exito);
        handleClose();
      }
    },
    onError: (error) => {
      const code = getErrorCode(error);
      if (code === 'EMAIL_EN_USO') setGlobalError(es.admin.usuarios.crear_dialog.errorEmailEnUso);
      else setGlobalError(getErrorMessage(error, es.errores.generico.algoSalioMal));
    },
  });

  function handleClose(): void {
    reset();
    setPasswordTemporal(null);
    setGlobalError(null);
    onOpenChange(false);
  }

  async function copyPasswordTemporal(): Promise<void> {
    if (passwordTemporal) {
      await navigator.clipboard.writeText(passwordTemporal);
      toast.success(es.admin.usuarios.crear_dialog.copiada);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : handleClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{es.admin.usuarios.crear_dialog.titulo}</DialogTitle>
          <DialogDescription>{es.admin.usuarios.crear_dialog.descripcion}</DialogDescription>
        </DialogHeader>

        {passwordTemporal ? (
          <div className="flex flex-col gap-4">
            <Alert variant="success" title={es.admin.usuarios.crear_dialog.passwordGeneradaTitulo}>
              {es.admin.usuarios.crear_dialog.passwordGeneradaDescripcion}
            </Alert>
            <div className="flex items-center gap-2 rounded-md border border-marca-neutro bg-marca-neutro/30 p-3">
              <code className="flex-1 break-all font-mono text-sm text-marca-gris">{passwordTemporal}</code>
              <Button type="button" variant="outline" size="sm" onClick={() => void copyPasswordTemporal()}>
                <Copy size={14} />
                {es.admin.usuarios.crear_dialog.copiar}
              </Button>
            </div>
            <DialogFooter>
              <Button onClick={handleClose}>{es.admin.usuarios.crear_dialog.cerrar}</Button>
            </DialogFooter>
          </div>
        ) : (
          <form
            onSubmit={(e) => void handleSubmit((d) => mutation.mutate(d))(e)}
            className="flex flex-col gap-4"
            noValidate
          >
            {globalError && <Alert variant="error">{globalError}</Alert>}

            <FormField
              label={es.admin.usuarios.crear_dialog.email}
              htmlFor="email"
              error={errors.email?.message}
            >
              <Input
                id="email"
                type="email"
                autoComplete="off"
                aria-invalid={!!errors.email}
                {...register('email')}
              />
            </FormField>

            <FormField
              label={es.admin.usuarios.crear_dialog.nombre}
              htmlFor="nombre"
              error={errors.nombre?.message}
            >
              <Input id="nombre" aria-invalid={!!errors.nombre} {...register('nombre')} />
            </FormField>

            <FormField label={es.admin.usuarios.crear_dialog.rol} htmlFor="rol" error={errors.rol?.message}>
              <Controller
                control={control}
                name="rol"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="rol" aria-invalid={!!errors.rol}>
                      <SelectValue placeholder={es.admin.usuarios.crear_dialog.rolPlaceholder} />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLES.map((rol) => (
                        <SelectItem key={rol} value={rol}>
                          {es.roles[rol]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </FormField>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                {es.admin.usuarios.crear_dialog.cancelar}
              </Button>
              <Button type="submit" disabled={isSubmitting || mutation.isPending}>
                {mutation.isPending
                  ? es.admin.usuarios.crear_dialog.creando
                  : es.admin.usuarios.crear_dialog.crear}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
